/**
 * Centralized Datadog RUM and Logs service for HostedX.
 *
 * Lifecycle: initDatadog (app startup) → handleSessionBoundary (after auth)
 *   → updateSessionContext (each step).
 *
 * Session splitting uses the server's `new_session` flag as the authoritative
 * signal. When the server creates a new access token, the client splits the
 * Datadog session so each HostedX journey gets its own RUM session and replay.
 */
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { v4 as uuid } from 'uuid';
import { reportError } from './error-reporting-service';
import sessionId from './session-service';

const HOSTEDX_VISIT_ID = 'core/hostedxVisitId';

// --- sessionStorage helpers (isolated cache to avoid clobbering localStorage wrapper) ---

const sessionCache: Record<string, string | null> = {};

function getSessionItem(key: string): string | null {
  const cached = sessionCache[key];
  if (cached) return cached;

  try {
    const value = window.sessionStorage.getItem(key);
    sessionCache[key] = value;
    return value;
  } catch (error) {
    reportError({ error });
    return null;
  }
}

function setSessionItem(key: string, value: string): void {
  sessionCache[key] = value;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    reportError({ error });
  }
}

// --- Visit ID management ---

function getOrCreateVisitId(): string {
  let id = getSessionItem(HOSTEDX_VISIT_ID);
  if (id == null) {
    id = uuid();
    setSessionItem(HOSTEDX_VISIT_ID, id);
  }
  return id;
}

function rotateVisitId(): string {
  const id = uuid();
  setSessionItem(HOSTEDX_VISIT_ID, id);
  return id;
}

// --- Public API ---

export interface DatadogEnvironment {
  dataDogApplicationId: string;
  dataDogClientToken: string;
  environment: string;
  releaseSha: string;
}

/**
 * Initialize Datadog RUM, Logs, and Session Replay, then tag early context
 * (URL params, client session ID) so even pre-auth events are attributable.
 */
export function initDatadog(env: DatadogEnvironment): void {
  datadogRum.init({
    applicationId: env.dataDogApplicationId,
    clientToken: env.dataDogClientToken,
    site: 'datadoghq.com',
    service: 'agencyapi-client',
    env: env.environment,
    version: env.releaseSha,
    enableExperimentalFeatures: ['feature_flags'],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    compressIntakeRequests: true,
    allowedTracingUrls: [
      /https?:\/\/localhost(:\d+)?/,
      /https?:\/\/(.+\.)?joinroot.com(:\d+)?/,
      /https?:\/\/(.+\.)?root.com(:\d+)?/,
    ],
    trackSessionAcrossSubdomains: true,
  });

  datadogLogs.init({
    clientToken: env.dataDogClientToken,
    site: 'datadoghq.com',
    service: 'agencyapi-client',
    env: env.environment,
    version: env.releaseSha,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    forwardReports: 'all',
    sessionSampleRate: 100,
  });

  datadogRum.startSessionReplayRecording();

  const params = new URLSearchParams(window.location.search);
  datadogRum.setGlobalContextProperty('clientSessionId', sessionId());
  datadogRum.setGlobalContextProperty('requestedExperience', params.get('experience'));
  datadogRum.setGlobalContextProperty('requestedUtmSource', params.get('utm_source'));
}

/**
 * Split the Datadog RUM session when the server created a new access token.
 * Uses `new_session` as the sole signal — sessionStorage-based detection was
 * unreliable across cross-origin navigations and new tabs in the agent flow.
 */
export function handleSessionBoundary(newSession: boolean): void {
  let visitId = getOrCreateVisitId();

  if (newSession) {
    visitId = rotateVisitId();
    datadogRum.stopSession();
  }

  datadogRum.setGlobalContextProperty('hostedxVisitId', visitId);
  datadogRum.setGlobalContextProperty('newHostedxSession', newSession);

  if (newSession) {
    datadogRum.addAction('HOSTEDX_SESSION_STARTED', { trigger: 'new_session' });
    // stopSession() ends replay recording; restart it for the new session
    datadogRum.startSessionReplayRecording();
  }
}

/**
 * Update RUM user context and feature flag evaluations after step data loads.
 * Called from trackScene with data from the server's scene response.
 */
export function updateSessionContext({
  quote_id,
  agent_id,
  market,
  experience,
  experiments,
}: {
  quote_id: string | null;
  agent_id: string | null;
  market: string | null;
  experience: string;
  experiments: Record<string, string>;
}): void {
  datadogRum.setUser({
    ...datadogRum.getUser(),
    id: sessionId(),
    quoteId: quote_id,
    agentId: agent_id,
    market,
    experience,
  });

  datadogRum.setGlobalContextProperty('experience', experience);

  Object.entries(experiments).forEach(([key, value]) => {
    datadogRum.addFeatureFlagEvaluation(key, value);
  });
}
